// /ugc — "Fake UGC". One-tap creator-style render: pick a product → the app
// renders it in an authentic phone-shot / UGC aesthetic (backend UGC recipes,
// no template picker) and saves it to My Creatives. Single option by design.
// Backend: routes/ugc.py (/api/ugc-creative/generate).
//
// UI mirrors the /appv3 Studio (Product Shot / Model Shot): full-width sticky
// header with a gradient icon tile, then two white-halo panels at xl:h-[800px]
// — LEFT product picker (dense tiles, full-width Generate CTA), RIGHT large
// preview / editor. Chrome, tile styling, button sizes + positions all match
// the studio.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import { useBrand } from '@/hooks/useBrand';
import { useCredits } from '@/hooks/useCredits';
import {
  listProducts,
  generateUgcCreative,
  type UserProduct,
  type UgcGenerateResult,
} from '@/lib/api';

const UGC_CREDITS = 1;

// Same white-halo chrome every Studio panel uses (Product Shot / Model Shot).
const PANEL = 'rounded-2xl shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)]';

export default function UgcPage() {
  const navigate = useNavigate();
  const { activeBrand } = useBrand();
  const { balance, refresh: refreshCredits } = useCredits();

  const [products, setProducts] = useState<UserProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<UgcGenerateResult | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoadingProducts(true);
    listProducts()
      .then((rows) => { if (!cancelled) setProducts(rows); })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setLoadingProducts(false); });
    return () => { cancelled = true; };
  }, [activeBrand?.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q) ||
      (p.productCategory || '').toLowerCase().includes(q),
    );
  }, [products, query]);

  const canGenerate = !!selectedId && !generating;
  const lowBalance = (balance ?? 0) < UGC_CREDITS;

  const handleGenerate = async () => {
    if (!selectedId) return;
    if (lowBalance) { navigate('/checkout'); return; }
    setGenerating(true);
    setError('');
    setResult(null);
    try {
      const res = await generateUgcCreative(selectedId);
      setResult(res);
      void refreshCredits();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <AppLayoutV2>
      {/* Full-width sticky header — same chrome as Product Shot / Model Shot. */}
      <div className="sticky top-0 z-30 bg-white border-b border-surface-dark/40 shadow-[0_2px_14px_-6px_rgba(15,15,30,0.18)]">
        <div className="w-full max-w-[1500px] mx-auto px-6 py-3 flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-hover text-white flex items-center justify-center shadow-md shadow-primary/25">
            <i className="ri-camera-lens-line text-xl" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-dark truncate leading-tight">
              Fake UGC
            </h1>
            <p className="hidden sm:block text-xs text-dark-muted truncate">
              Pick a product — we'll render it as an authentic, phone-shot creator post.
            </p>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1500px] mx-auto px-6 pt-6 pb-8">
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,589px)_minmax(0,843px)] gap-5 items-stretch xl:h-[800px]">
          {/* ============ LEFT — product picker ============ */}
          <div className={`${PANEL} bg-white flex flex-col overflow-hidden min-h-[520px] xl:h-full`}>
            <div className="px-5 pt-5 pb-3 flex items-center justify-between gap-3 flex-wrap flex-shrink-0">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-dark">
                Select a product
              </h2>
              {products.length > 6 && (
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search products…"
                  className="px-3 py-1.5 rounded-full border border-dark-muted/40 text-sm w-48 focus:outline-none focus:border-primary"
                />
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-5 pr-4">
              {loadingProducts ? (
                <p className="text-sm text-dark-muted py-8 text-center">Loading your products…</p>
              ) : products.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-dark-muted text-sm mb-3">
                    No products yet. Add products first, then come back to make UGC.
                  </p>
                  <button
                    onClick={() => navigate('/products')}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-white text-sm font-bold"
                  >
                    Go to My Products
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 pb-2">
                  {filtered.map((p) => {
                    const isSelected = p.id === selectedId;
                    return (
                      <button
                        key={p.id}
                        onClick={() => setSelectedId(p.id)}
                        title={p.name ?? 'Use this product'}
                        className="group flex flex-col gap-1 text-left cursor-pointer"
                      >
                        <div className={`
                          relative aspect-square rounded-xl overflow-hidden
                          border-2 transition-all bg-surface/40
                          ${isSelected
                            ? 'border-primary shadow-md shadow-primary/25 ring-2 ring-primary/30'
                            : 'border-surface-dark/30 group-hover:border-primary/60 group-hover:shadow-md group-hover:shadow-primary/15'}
                        `}>
                          {p.imageUrl ? (
                            <img
                              src={p.imageUrl}
                              alt={p.name || 'Product'}
                              loading="lazy"
                              draggable={false}
                              className="absolute inset-0 w-full h-full object-contain p-1.5"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-dark-muted/40">
                              <i className="ri-image-line text-2xl" />
                            </div>
                          )}
                          {isSelected && (
                            <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-md">
                              <i className="ri-check-line text-xs" />
                            </div>
                          )}
                        </div>
                        <span
                          className={`block text-xs leading-snug px-0.5 mt-0.5 ${
                            isSelected ? 'text-primary font-bold' : 'text-dark font-semibold'
                          }`}
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {p.name || 'Untitled'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {error && (
              <div className="mx-5 mb-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex-shrink-0">
                {error}
              </div>
            )}

            {products.length > 0 && (
              <div className="border-t border-surface-dark/30 px-5 py-4 flex-shrink-0">
                <button
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                  className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-base font-bold shadow-lg transition-all cursor-pointer disabled:cursor-not-allowed ${
                    lowBalance
                      ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/25'
                      : 'bg-primary hover:bg-primary-hover text-white shadow-primary/25 disabled:opacity-50'
                  }`}
                >
                  {generating ? (
                    <><i className="ri-loader-4-line animate-spin" /> Generating…</>
                  ) : lowBalance ? (
                    <><i className="ri-vip-crown-line" /> Upgrade to generate</>
                  ) : (
                    <>
                      <i className="ri-sparkling-2-line" /> Generate UGC
                      <span className="ml-1 px-2 py-0.5 rounded-full bg-white/20 text-white text-xs font-bold">
                        {UGC_CREDITS} credit
                      </span>
                    </>
                  )}
                </button>
                <p className="text-xs text-dark-muted text-center mt-1.5">
                  {UGC_CREDITS} credit · you have {balance ?? 0}
                </p>
              </div>
            )}
          </div>

          {/* ============ RIGHT — preview / editor ============ */}
          <div className={`${PANEL} overflow-hidden flex flex-col min-h-[520px] xl:h-full bg-gradient-to-br from-surface to-surface-dark/40`}>
            <div className="flex-1 min-h-0 flex items-center justify-center p-4">
              {result ? (
                <img
                  src={result.image_url}
                  alt={result.title}
                  className="max-h-full max-w-full object-contain rounded-xl border border-surface-dark/20 shadow-lg shadow-dark/10"
                />
              ) : generating ? (
                <div className="text-center px-6">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <i className="ri-camera-lens-line text-3xl" />
                  </div>
                  <p className="text-dark font-semibold">Shooting your UGC…</p>
                  <p className="text-sm text-dark-muted mt-1">
                    Dialing in realistic lighting — this takes ~20 seconds.
                  </p>
                </div>
              ) : (
                <div className="text-center px-6">
                  <div className="w-16 h-16 rounded-2xl bg-white/70 text-dark-muted flex items-center justify-center mx-auto mb-4">
                    <i className="ri-image-line text-3xl" />
                  </div>
                  <p className="text-dark font-semibold">Your UGC render appears here</p>
                  <p className="text-sm text-dark-muted mt-1">
                    Pick a product on the left, then hit Generate.
                  </p>
                </div>
              )}
            </div>

            {result && (
              <div className="border-t border-white/60 bg-white/80 backdrop-blur-sm px-4 py-3 flex items-center gap-3 flex-shrink-0">
                <span className="text-xs text-dark-muted">Saved to My Creatives.</span>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    onClick={() => setResult(null)}
                    className="h-11 px-5 inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark text-xs font-bold uppercase tracking-[0.08em] transition-all cursor-pointer"
                  >
                    <i className="ri-refresh-line text-base" /> Make another
                  </button>
                  <button
                    onClick={() => navigate(`/creatives/${result.post_id}`)}
                    className="h-11 px-5 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold uppercase tracking-[0.08em] shadow-md shadow-primary/25 transition-all cursor-pointer"
                  >
                    <i className="ri-edit-line text-base" /> Open in editor
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayoutV2>
  );
}
